import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "@/lib/LangContext";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogIn, LogOut, RefreshCcw, Printer } from "lucide-react";
import { Link } from "react-router-dom";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];

function StatusBadge({ status }) {
  const map = {
    pending: "bg-neutral-100 text-neutral-800",
    confirmed: "bg-blue-100 text-[#6B1F27]",
    in_progress: "bg-amber-100 text-amber-900",
    completed: "bg-emerald-100 text-emerald-900",
    cancelled: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-flex px-2 py-1 text-[11px] font-mono-tight uppercase tracking-wider ${map[status] || ""}`}>
      {status.replace("_", " ")}
    </span>
  );
}

export default function Admin() {
  const { t, lang } = useLang();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [techEdits, setTechEdits] = useState({});

  const load = useCallback(async () => {
    try {
      const [meRes, apRes, stRes] = await Promise.all([
        http.get("/auth/me"),
        http.get("/appointments"),
        http.get("/appointments/stats"),
      ]);
      setUser(meRes.data);
      setAppointments(apRes.data);
      setStats(stRes.data);
    } catch (e) {
      setUser(null);
      setAppointments([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await http.post("/auth/logout");
    } finally {
      setUser(null);
      setAppointments([]);
      setStats(null);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await http.patch(`/appointments/${id}`, { status });
      toast.success(lang === "es" ? "Estado actualizado" : "Status updated");
      load();
    } catch {
      toast.error(lang === "es" ? "Error al actualizar" : "Update failed");
    }
  };

  const saveTech = async (id) => {
    const tech = techEdits[id];
    if (tech === undefined) return;
    try {
      await http.patch(`/appointments/${id}`, { status: appointments.find((a) => a.id === id).status, technician: tech });
      toast.success(lang === "es" ? "Técnico asignado" : "Technician assigned");
      load();
    } catch {
      toast.error(lang === "es" ? "Error" : "Error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header active="admin" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="tick-label">Loading…</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header active="admin" />
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <div className="tick-label">Admin · CyberRepair</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] mt-6">
              {t.admin.title}
            </h1>
            <p className="text-neutral-600 mt-6 max-w-md">{t.admin.only_owner}</p>
            <button
              data-testid="admin-login-btn"
              onClick={handleLogin}
              className="btn-primary mt-10"
            >
              <LogIn size={18} /> {t.admin.login}
            </button>
          </div>
          <div className="lg:col-span-6 hairline bg-white p-8">
            <div className="tick-label mb-3">cyberrepaircorp@gmail.com</div>
            <p className="text-sm text-neutral-600">
              {lang === "es"
                ? "Solo el propietario puede acceder al panel. La autenticación se maneja con Google via Emergent Auth."
                : "Only the owner may access the panel. Auth is handled by Google via Emergent Auth."}
            </p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header active="admin" />
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="tick-label">403</div>
          <h1 className="font-display text-4xl tracking-tighter mt-4">{t.admin.only_owner}</h1>
          <p className="text-neutral-600 mt-4">Signed in as: {user.email}</p>
          <button onClick={handleLogout} className="btn-ghost mt-8" data-testid="admin-logout-btn">
            <LogOut size={18} /> {t.nav.logout}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header active="admin" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="tick-label" data-testid="admin-user-email">{user.email}</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-3">
              {t.admin.title}
            </h1>
            <p className="text-neutral-600 mt-2">{t.admin.subtitle}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/qr" className="btn-ghost text-sm" data-testid="admin-qr-link">
              <Printer size={16} /> QR
            </Link>
            <button onClick={load} className="btn-ghost text-sm" data-testid="admin-refresh">
              <RefreshCcw size={16} /> {lang === "es" ? "Actualizar" : "Refresh"}
            </button>
            <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="admin-logout-btn">
              <LogOut size={16} /> {t.nav.logout}
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="admin-stats">
            <Stat label={t.admin.total} value={stats.total} accent />
            <Stat label={t.admin.pending} value={stats.pending} />
            <Stat label={t.admin.confirmed} value={stats.confirmed} />
            <Stat label={t.admin.in_progress} value={stats.in_progress} />
            <Stat label={t.admin.completed} value={stats.completed} />
            <Stat label={t.admin.cancelled} value={stats.cancelled} />
          </div>
        )}

        <div className="mt-10 hairline bg-white">
          <Table data-testid="admin-appointments-table">
            <TableHeader>
              <TableRow>
                <TableHead className="tick-label">{t.admin.customer}</TableHead>
                <TableHead className="tick-label">{t.admin.device}</TableHead>
                <TableHead className="tick-label">{t.admin.service}</TableHead>
                <TableHead className="tick-label">{t.admin.when}</TableHead>
                <TableHead className="tick-label">{t.admin.status}</TableHead>
                <TableHead className="tick-label">{t.admin.technician}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-neutral-500">
                    {t.admin.no_data}
                  </TableCell>
                </TableRow>
              )}
              {appointments.map((a) => (
                <TableRow key={a.id} data-testid={`row-${a.id}`}>
                  <TableCell>
                    <div className="font-semibold">{a.customer_name}</div>
                    <div className="text-xs text-neutral-500">{a.customer_email}</div>
                    <div className="text-xs text-neutral-500">{a.customer_phone}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono-tight text-xs uppercase">{a.device_type}</div>
                    <div className="text-sm">{a.device_brand} {a.device_model}</div>
                    <div className="text-xs text-neutral-500 mt-1 max-w-xs truncate">{a.issue_description}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{a.service_type === "at_home" ? t.booking.at_home : t.booking.in_shop}</div>
                    {a.address && <div className="text-xs text-neutral-500 max-w-xs">{a.address}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="font-mono-tight text-sm">{a.appointment_date}</div>
                    <div className="text-xs text-neutral-500">{a.appointment_time}</div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <StatusBadge status={a.status} />
                      <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                        <SelectTrigger data-testid={`status-select-${a.id}`} className="rounded-none h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}-${a.id}`}>{s.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      data-testid={`tech-input-${a.id}`}
                      className="rounded-none h-8 text-sm"
                      defaultValue={a.technician || ""}
                      onChange={(e) => setTechEdits((s) => ({ ...s, [a.id]: e.target.value }))}
                      onBlur={() => saveTech(a.id)}
                      placeholder="—"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`hairline p-5 ${accent ? "bg-[#3D1015] text-white" : "bg-white"}`}>
      <div className={`tick-label ${accent ? "!text-neutral-400" : ""}`}>{label}</div>
      <div className="font-display text-3xl md:text-4xl tracking-tighter mt-2">{value}</div>
    </div>
  );
}
